import { useMemo, useRef, useState, type JSX } from 'react';
import { AlertTriangle, Check, ImagePlus, Images, MessageSquare, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import { POSITIVE_REPLY, SCREENSHOT_RULE, STEPS } from '@/lib/outreach-copy';
import { cn } from '@/lib/utils';
import type { BulkAction } from '@/lib/bulk-import/copy';
import type { ImportRow, RowStatus } from '@/lib/bulk-import/rows';
import { SCREENSHOT_ACCEPT, matchScreenshots, screenshotProblem } from '@/lib/bulk-import/screenshots';
import { HandleCell, StepPanel, plural } from './shared';

const PAGE_SIZE = 25;

/** Rows "Mark as messaged" skips: already marked, or already logged by you. */
export function messagedSkipReason(status: RowStatus): string | null {
  if (status === 'messaged_by_me') return 'Already marked as messaged';
  if (status === 'mine') return 'Already yours';
  return null;
}

interface ActionStepProps {
  rows: ImportRow[];
  statusOf: (row: ImportRow) => RowStatus;
  action: BulkAction;
  onAction: (a: BulkAction) => void;
  screenshotRequired: boolean;
  screenshots: Map<number, File>;
  onScreenshots: (next: Map<number, File>) => void;
  /** Creator types the team can log. A picker shows when there is more than one. */
  categories: Array<{ value: string; label: string }>;
  category: string;
  onCategory: (c: string) => void;
  /** Untick rows (used to drop rows still missing a required screenshot). */
  onUntick: (keys: number[]) => void;
  onBack: () => void;
  onRun: () => void;
}

export function ActionStep(props: ActionStepProps): JSX.Element {
  const { rows, action, screenshots, screenshotRequired } = props;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const skipped = rows.filter((r) => messagedSkipReason(props.statusOf(r)) !== null).length;
  const toMessage = rows.length - skipped;
  const withShot = rows.filter((r) => screenshots.has(r.key)).length;
  const missing = rows.length - withShot;
  const needsCategory = action === 'log' && props.categories.length > 1 && !props.category;
  const blocked =
    action === 'messaged' ? toMessage === 0 : rows.length === 0 || (screenshotRequired && missing > 0) || needsCategory;

  const runLabel = action === 'messaged' ? `Mark ${plural(toMessage, 'creator')} as messaged` : `Log ${plural(rows.length, 'reply', 'replies')}`;

  return (
    <div className="space-y-6">
      <StepPanel title="What do you want to do?" description={`${plural(rows.length, 'creator')} ticked.`}>
        <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Action">
          <ActionCard
            selected={action === 'messaged'}
            onSelect={() => props.onAction('messaged')}
            icon={<MessageSquare className="h-4 w-4" />}
            title="Mark as messaged"
            body={`${STEPS.messaged.means} Use this for creators you've DM'd who haven't answered yet.`}
          />
          <ActionCard
            selected={action === 'log'}
            onSelect={() => props.onAction('log')}
            icon={<Send className="h-4 w-4" />}
            title="Log replies"
            body={`${STEPS.logged.means} Only for creators who replied asking how to join.`}
          />
        </div>
      </StepPanel>

      {action === 'messaged' ? (
        <StepPanel title="Mark as messaged">
          <p className="text-sm">
            <span className="font-semibold">{plural(toMessage, 'creator')}</span> will be marked as messaged.
            {skipped > 0 && (
              <span className="text-muted-foreground">
                {' '}
                {plural(skipped, 'creator')} you&apos;ve already marked or logged will be skipped.
              </span>
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            This isn&apos;t a log and earns nothing. It tells your team you&apos;ve messaged them. When one of them replies
            asking how to join, log the reply.
          </p>
        </StepPanel>
      ) : (
        <>
          <div className="rounded-lg border-2 border-warning/50 bg-warning/[0.08] px-4 py-3">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
              <div className="space-y-1">
                <p className="text-base font-bold text-foreground">Only log creators who replied asking how to join.</p>
                <p className="text-sm text-foreground/90">{POSITIVE_REPLY.headline}</p>
                <p className="text-sm text-foreground/90">
                  <span className="font-semibold text-foreground">Doesn&apos;t count:</span> {POSITIVE_REPLY.doesNot}
                </p>
                <p className="text-sm text-foreground/90">
                  Everyone else in the file should be marked as messaged instead. Untick them in Review.
                </p>
              </div>
            </div>
          </div>

          {props.categories.length > 1 && (
            <StepPanel title="Creator type" description="Every reply in this batch is logged with this creator type.">
              <div className="grid max-w-xs gap-1.5">
                <Label htmlFor="bulk-category">Creator type</Label>
                <Combobox
                  id="bulk-category"
                  value={props.category}
                  onChange={props.onCategory}
                  options={props.categories}
                  placeholder="Pick a creator type"
                />
              </div>
            </StepPanel>
          )}

          <ScreenshotsPanel
            rows={rows}
            screenshots={screenshots}
            onScreenshots={props.onScreenshots}
            required={screenshotRequired}
          />
        </>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {action === 'log' && screenshotRequired && missing > 0
            ? `Your team needs a screenshot for every reply. ${plural(missing, 'row')} still need one.`
            : needsCategory
              ? 'Pick a creator type first.'
              : 'You confirm before anything is sent.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={props.onBack}>
            Back
          </Button>
          {action === 'log' && screenshotRequired && missing > 0 && withShot > 0 && (
            <Button
              variant="outline"
              onClick={() => props.onUntick(rows.filter((r) => !screenshots.has(r.key)).map((r) => r.key))}
            >
              Leave out {plural(missing, 'row')} without one
            </Button>
          )}
          <Button onClick={() => setConfirmOpen(true)} disabled={blocked}>
            {runLabel}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={action === 'messaged' ? `Mark ${plural(toMessage, 'creator')} as messaged?` : `Log ${plural(rows.length, 'reply', 'replies')}?`}
        description={
          action === 'messaged' ? (
            <>
              <span className="block">{STEPS.messaged.means}</span>
              {skipped > 0 && <span className="mt-2 block">{plural(skipped, 'creator')} already marked or logged will be skipped.</span>}
            </>
          ) : (
            <>
              <span className="block">
                This creates {plural(rows.length, 'invite')}, one for each creator, exactly as if you&apos;d used Log reply for
                each of them.
              </span>
              <span className="mt-2 block font-semibold text-foreground">
                Only log creators who replied asking how to join. Logging anyone else is a false log.
              </span>
              {missing > 0 && (
                <span className="mt-2 block">
                  {plural(missing, 'reply', 'replies')} without a screenshot. A screenshot is needed to get paid; add them
                  later from My creators.
                </span>
              )}
            </>
          )
        }
        confirmLabel={runLabel}
        onConfirm={() => props.onRun()}
      />
    </div>
  );
}

function ActionCard({
  selected,
  onSelect,
  icon,
  title,
  body,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: JSX.Element;
  title: string;
  body: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex items-start gap-3 rounded-xl border p-4 text-left transition-colors',
        selected ? 'border-brand/50 bg-brand/[0.07]' : 'border-border hover:bg-accent/40',
      )}
    >
      <span
        className={cn(
          'grid h-9 w-9 shrink-0 place-items-center rounded-lg border',
          selected ? 'border-brand/30 bg-brand/15 text-brand' : 'border-border text-muted-foreground',
        )}
      >
        {icon}
      </span>
      <span className="space-y-1">
        <span className="flex items-center gap-2 text-sm font-semibold">
          {title}
          {selected && <Check className="h-3.5 w-3.5 text-brand" />}
        </span>
        <span className="block text-xs text-muted-foreground">{body}</span>
      </span>
    </button>
  );
}

// ── Screenshots ───────────────────────────────────────────────────────────

function ScreenshotsPanel({
  rows,
  screenshots,
  onScreenshots,
  required,
}: {
  rows: ImportRow[];
  screenshots: Map<number, File>;
  onScreenshots: (next: Map<number, File>) => void;
  required: boolean;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const [missingOnly, setMissingOnly] = useState(false);
  const [unmatched, setUnmatched] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const rowInput = useRef<HTMLInputElement>(null);
  const rowTarget = useRef<number | null>(null);

  const withShot = rows.filter((r) => screenshots.has(r.key)).length;
  const shown = useMemo(() => (missingOnly ? rows.filter((r) => !screenshots.has(r.key)) : rows), [rows, screenshots, missingOnly]);
  const pageRows = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const rowsWithout = rows.filter((r) => !screenshots.has(r.key));

  function addFiles(list: FileList | File[]): void {
    const files = Array.from(list);
    const bad = files.filter((f) => screenshotProblem(f));
    if (bad.length > 0) {
      toast.error(
        `${plural(bad.length, 'file')} skipped: ${screenshotProblem(bad[0]!)}${bad.length > 1 ? ' (and others)' : ''}. ${SCREENSHOT_RULE.formats}`,
      );
    }
    const good = files.filter((f) => !screenshotProblem(f));
    if (good.length === 0) return;
    const outcome = matchScreenshots(
      good.map((f) => f.name),
      rows.filter((r) => r.username).map((r) => ({ key: r.key, username: r.username! })),
    );
    const next = new Map(screenshots);
    for (const [fileIdx, key] of outcome.matched) next.set(key, good[fileIdx]!);
    onScreenshots(next);
    const leftover = [...outcome.unmatched, ...outcome.extra].map((i) => good[i]!);
    setUnmatched((prev) => [...prev, ...leftover]);
    toast.success(`Matched ${plural(outcome.matched.size, 'screenshot')}${leftover.length ? `. ${leftover.length} didn't match a handle` : ''}.`);
  }

  function setOne(key: number, file: File | null): void {
    const next = new Map(screenshots);
    if (file) next.set(key, file);
    else next.delete(key);
    onScreenshots(next);
  }

  return (
    <StepPanel
      title="Screenshots"
      description={
        required
          ? 'Your team needs a screenshot of each creator asking how to join. Every row needs one before it can be logged.'
          : "Optional for your team, but a screenshot is needed to get paid. You can add missing ones later from My creators."
      }
      actions={
        <span className={cn('text-sm font-semibold tabular-nums', required && withShot < rows.length ? 'text-warning' : 'text-foreground')}>
          {withShot.toLocaleString('en-US')} of {rows.length.toLocaleString('en-US')} have one
        </span>
      }
    >
      <p className="text-xs font-medium text-warning">{SCREENSHOT_RULE.short}</p>

      <label
        htmlFor="bulk-shots"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors',
          dragging ? 'border-brand/60 bg-brand/[0.06]' : 'border-border hover:border-brand/40 hover:bg-accent/30',
        )}
      >
        <Images className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm font-semibold">Match screenshots: drop many images at once</span>
        <span className="max-w-[60ch] text-xs text-muted-foreground">
          Name each file after the creator&apos;s handle (for example <span className="font-mono">orzedits0.png</span>) and
          they&apos;re matched for you. Case, @ and punctuation don&apos;t matter. {SCREENSHOT_RULE.formats}
        </span>
        <input
          id="bulk-shots"
          type="file"
          multiple
          accept={SCREENSHOT_ACCEPT}
          className="sr-only"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </label>

      {unmatched.length > 0 && (
        <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/[0.06] p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold">
              {plural(unmatched.length, 'file')} didn&apos;t match a handle. Pick the creator for each, or dismiss it.
            </p>
            <Button variant="ghost" size="sm" onClick={() => setUnmatched([])}>
              Dismiss all
            </Button>
          </div>
          <ul className="space-y-2">
            {unmatched.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex flex-wrap items-center gap-2">
                <span className="w-full truncate font-mono text-xs sm:w-auto sm:min-w-0 sm:flex-1" title={f.name}>
                  {f.name}
                </span>
                <Combobox
                  className="min-w-0 flex-1 sm:w-64 sm:flex-none"
                  aria-label={`Creator for ${f.name}`}
                  value=""
                  onChange={(v) => {
                    if (v === '') return;
                    setOne(Number(v), f);
                    setUnmatched((prev) => prev.filter((_, j) => j !== i));
                  }}
                  options={rowsWithout.map((r) => ({ value: String(r.key), label: `@${r.username ?? r.rawHandle}` }))}
                  placeholder="Pick a creator"
                  emptyText="Every row has a screenshot"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Dismiss ${f.name}`}
                  title="Dismiss"
                  onClick={() => setUnmatched((prev) => prev.filter((_, j) => j !== i))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--brand)]"
            checked={missingOnly}
            onChange={(e) => {
              setMissingOnly(e.target.checked);
              setPage(1);
            }}
          />
          Only rows without a screenshot
        </label>
      </div>

      <input
        ref={rowInput}
        type="file"
        accept={SCREENSHOT_ACCEPT}
        className="sr-only"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => {
          const f = e.target.files?.[0];
          const key = rowTarget.current;
          e.target.value = '';
          if (!f || key === null) return;
          const problem = screenshotProblem(f);
          if (problem) {
            toast.error(`${problem}. ${SCREENSHOT_RULE.formats}`);
            return;
          }
          setOne(key, f);
        }}
      />

      {shown.length > 0 && (
        <>
          <DataTable density="outreacher" aria-label="Screenshots per creator">
            <DataTableHeader>
              <tr>
                <DataTableHeaderCell>Creator</DataTableHeaderCell>
                <DataTableHeaderCell>Screenshot</DataTableHeaderCell>
                <DataTableHeaderCell className="text-right">
                  <span className="sr-only">Actions</span>
                </DataTableHeaderCell>
              </tr>
            </DataTableHeader>
            <tbody>
              {pageRows.map((r) => {
                const f = screenshots.get(r.key);
                return (
                  <DataTableRow key={r.key}>
                    <DataTableCell anchor>
                      <HandleCell row={r} />
                    </DataTableCell>
                    <DataTableCell data-label="Screenshot">
                      {f ? (
                        <span className="flex items-center gap-1.5">
                          <Check className="h-3.5 w-3.5 shrink-0 text-success" />
                          <span className="max-w-[28ch] truncate font-mono text-xs" title={f.name}>
                            {f.name}
                          </span>
                        </span>
                      ) : (
                        <span className={cn('text-xs', required ? 'font-medium text-warning' : 'text-muted-foreground')}>
                          {required ? 'Needed' : 'None'}
                        </span>
                      )}
                    </DataTableCell>
                    <DataTableCell actions>
                      <span className="inline-flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            rowTarget.current = r.key;
                            rowInput.current?.click();
                          }}
                        >
                          <ImagePlus className="h-3.5 w-3.5" />
                          {f ? 'Change' : 'Add screenshot'}
                        </Button>
                        {f && (
                          <Button variant="ghost" size="sm" onClick={() => setOne(r.key, null)}>
                            Remove
                          </Button>
                        )}
                      </span>
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
            </tbody>
          </DataTable>
          <Pagination page={page} limit={PAGE_SIZE} total={shown.length} onPageChange={setPage} />
        </>
      )}
    </StepPanel>
  );
}
